import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useStepProcessor } from '../../context/StepProcessorContext';
import { verifyOTP, saveAuthToken } from '../../services/AuthService';
import { toast } from 'react-toastify';

const OtpStep: React.FC = () => {
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState<string>('');
  const [countdown, setCountdown] = useState(60);
  const { goToPreviousStep, completeStep } = useStepProcessor();
  const navigate = useNavigate();

  useEffect(() => {
    const storedEmail = localStorage.getItem('auth_email');
    if (!storedEmail) {
      goToPreviousStep();
      toast.error('Email information missing');
      return;
    }
    setEmail(storedEmail);

    // Start countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [goToPreviousStep]);

  const handleResendOtp = async () => {
    // This would typically call the sendOTP function again
    // For now, we'll just reset the countdown
    setCountdown(60);
    toast.info('OTP resent to your email');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setIsLoading(true);
    try {
      const response = await verifyOTP(email, otp);
      
      if (response.status.success && response.data?.jwt_auth) {
        // Save the auth token
        saveAuthToken(response.data.jwt_auth);
        completeStep('otp');
        toast.success('Authentication successful');
        
        // Get any stored pod selection from localStorage
        const selectedPodId = localStorage.getItem('selected_pod_id');
        const selectedSlotIndex = localStorage.getItem('selected_slot_index');
        
        // Navigate to homepage after successful authentication
        setTimeout(() => {
          navigate('/homepage', {
            replace: true,
            state: {
              selectedPodId,
              selectedSlotIndex: selectedSlotIndex ? parseInt(selectedSlotIndex) : undefined
            }
          });
        }, 1000);
      } else {
        toast.error(response.status.errorMessage || 'Invalid OTP');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      toast.error('An error occurred while verifying OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow digits and limit to 6 characters
    if (/^\d*$/.test(value) && value.length <= 6) {
      setOtp(value);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center justify-center p-4"
    >
      <div className="w-full max-w-md">
        <h2 className="text-xl font-semibold mb-2 text-center">OTP Verification</h2>
        <p className="text-center text-gray-600 mb-6">
          Enter the 6-digit code sent to {email}
        </p>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">
              One-Time Password
            </label>
            <input
              id="otp"
              type="text"
              value={otp}
              onChange={handleOtpChange}
              placeholder="Enter 6-digit OTP"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-customBlue text-center text-xl tracking-wider"
              required
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-2 px-4 rounded-md text-white font-medium ${
              isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-customBlue hover:bg-blue-700'
            }`}
          >
            {isLoading ? 'Verifying...' : 'Verify OTP'}
          </button>
        </form>

        <div className="mt-4 text-center">
          {countdown > 0 ? (
            <p className="text-sm text-gray-600">
              Resend OTP in {countdown} seconds
            </p>
          ) : (
            <button
              onClick={handleResendOtp}
              className="text-customBlue hover:underline text-sm"
            >
              Resend OTP
            </button>
          )}
        </div>

        <div className="mt-4 text-center">
          <button
            onClick={goToPreviousStep}
            className="text-customBlue hover:underline text-sm"
          >
            Change Email
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default OtpStep;
